import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import ProSelectField from "../../Components/Inputs/SelectField";
import DateField from "../../Components/Inputs/DateField";
import { useForm } from "react-hook-form";
import { ADD_APPENDIX_D_FORM, GET_ALL_PROJECT, GET_ALL_PARTNERS_DROPDOWN, MISSING_APPENDIX_D_JOB_TITLES } from "../../../utils/services/apiPath";
import { getApi, postApi } from "../../../utils/services";
import { convertFileToBase64Model, prepareDefaultValues, prepareInitialConfig } from "../../../utils/formHelper";
import { Box, Button, Grid, IconButton, Modal, Typography } from "@mui/material";
import { Close, FileUpload } from "@mui/icons-material";
import fileUpload from "../../Assets/Icons/FileUpload.png";
import ProButton from "../ProButton";
import FileUploadBox from "./FileUploadBox";

const uploadPopupContainer = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 800,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4
};

const davisBaconFormFields = {
  projectId: {
    label: "Project Name",
    perKey: "Project",
    placeholder: "Select Project",
    rules: { required: "Project Name is Required!" }
  },
  partnerId: {
    label: "Partner Name",
    perKey: "Partner",
    placeholder: "Select Partners",
    rules: { required: "Partner Name is Required!" }
  },
  jobTitleId: {
    label: "Labor Classification Title",
    perKey: "jobTitleId",
    placeholder: "Select Labor Classification Title",
    rules: { required: "Labor Classification Title is Required!" }
  },
  sponsor: {
    label: "Sponsor",
    perKey: "sponsor",
    placeholder: "Select Sponsor",
    rules: { required: "Sponsor is Required!" },
    mode: "read"
  },
  signingDate: {
    label: "Signing Date",
    perKey: "signingDate",
    defaultValue: null,
    placeholder: "Signing Date",
    rules: { required: "Signing Date is Required!" }
  }
};

export default function UploadFileAppendixD({ title, getDetailData }) {
  const [formConfig] = useState(prepareInitialConfig(davisBaconFormFields, "edit"));
  const { handleSubmit, control, watch, setValue } = useForm({ defaultValues: prepareDefaultValues(davisBaconFormFields) });
  const [projectsList, setProjectsList] = useState([]);
  const [jobTitleList, setJobTitleList] = useState([]);
  const [sponsorList, setSponsorList] = useState([]);
  const [partnersList, setPartnersList] = useState([]);
  const [file, setFile] = useState(null);
  const [open, setOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const project = watch("projectId");
  const partner = watch("partnerId");
  const jobTitle = watch("jobTitleId");
  const signingDate = watch("signingDate");

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleFileSelect = selectedFile => {
    setErrorMessage("");
    setFile(selectedFile);
  };

  const fetchProjects = async () => {
    const { data } = await getApi(GET_ALL_PROJECT);
    setProjectsList(
      data
        ?.filter(item => item.projectId !== "All")
        .map(item => ({
          label: item.projectName,
          value: item.projectId
        }))
    );
  };

  const fetchPartners = async () => {
    try {
      const res = await getApi(`${GET_ALL_PARTNERS_DROPDOWN}?projectId=${project}`);
      const data = Array.isArray(res) ? res : res?.data;

      setPartnersList(
        Array.isArray(data)
          ? data
              .filter(item => item.clientGuid !== "All")
              .map(({ companyName, clientGuid }) => ({
                label: companyName,
                value: clientGuid
              }))
          : []
      );
    } catch {
      setPartnersList([]);
    }
  };

  const fetchJobTitlesAndSponsors = async () => {
    try {
      // Clear any previous error message at the start of the fetch
      setErrorMessage("");

      const endpoint = `${MISSING_APPENDIX_D_JOB_TITLES}?projectGuid=${project}&partnerGuid=${partner}`;
      const response = await getApi(endpoint);

      // Normalize the data structure with a clear variable name
      const jobTitlesData = response?.data || [];

      // Early return if no data - use isEmpty helper if available in your codebase
      if (!jobTitlesData.length) {
        // Reset state once instead of multiple times
        resetData();
        setErrorMessage(
          "No apprentices are assigned to any multi-employer RAPs for this Project and Partner. Please check Project and Partner to proceed."
        );
        return;
      }

      // Map job titles directly - no need for an identity transformation
      setJobTitleList(jobTitlesData);

      // Create a unique sponsor list more efficiently
      const uniqueSponsors = new Map();

      jobTitlesData.forEach(({ projectJobTitleGuid, projectSponsorName }) => {
        // Only add if not already in the map
        if (!uniqueSponsors.has(projectJobTitleGuid)) {
          uniqueSponsors.set(projectJobTitleGuid, {
            label: projectSponsorName,
            value: projectJobTitleGuid
          });
        }
      });

      setSponsorList(Array.from(uniqueSponsors.values()));
    } catch (error) {
      console.error("❌ Error fetching Labor Classification titles and sponsors:", error);
      resetData();
      setErrorMessage("An error occurred while fetching Labor Classification titles. Please try again.");
    }
  };

  // Extract resetting data to a helper function to avoid repetition
  const resetData = () => {
    setJobTitleList([]);
    setSponsorList([]);
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (project) {
      fetchPartners();
    }
  }, [project]);

  useEffect(() => {
    if (partner) {
      fetchJobTitlesAndSponsors();
    }
  }, [partner]);

  useEffect(() => {
    if (jobTitle) {
      setValue("sponsor", jobTitle);
    }
  }, [jobTitle]);

  const defaultFormProps = { control };
  const handleSave = async () => {
    if (!file) {
      setErrorMessage("Appendix D Form is Required!");
      return;
    }
    const payload = {
      projectId: project,
      jobTitleId: jobTitle,
      partnerId: partner,
      signingDate: signingDate,
      documentMediaModel: await convertFileToBase64Model(file)
    };
    const res = await postApi(ADD_APPENDIX_D_FORM, payload);
    getDetailData();
    handleClose();
  };
  return (
    <>
      <Button id="upload-files" variant="outlined" size="small" sx={{ width: "150px" }} onClick={handleOpen}>
        Upload
        <FileUpload />
      </Button>
      <Modal open={open} onClose={handleClose}>
        <Box sx={uploadPopupContainer}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6" component="h2">
                {title}
              </Typography>
              <IconButton aria-label="close" onClick={handleClose} sx={{ position: "absolute", right: 16, top: 16 }}>
                <Close />
              </IconButton>
            </Grid>
            <ProSelectField
              {...defaultFormProps}
              {...formConfig.projectId}
              options={projectsList}
              permission={{ canView: true, canAdd: true, canEdit: true }}
            />
            <ProSelectField
              {...defaultFormProps}
              {...formConfig.partnerId}
              options={partnersList}
              permission={{ canView: true, canAdd: true, canEdit: true }}
            />
            <ProSelectField
              {...defaultFormProps}
              {...formConfig.jobTitleId}
              options={jobTitleList}
              permission={{ canView: true, canAdd: true, canEdit: true }}
            />
            <DateField {...defaultFormProps} {...formConfig.signingDate} permission={{ canView: true, canAdd: true, canEdit: true }} />
            <Grid item xs={12}>
              <FileUploadBox onFileSelect={handleFileSelect} fileUpload={fileUpload} file={file} errorMessage={errorMessage} />
            </Grid>

            <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end", gap: "16px" }}>
              <ProButton variant="outlined" color="secondary" onClick={handleClose}>
                {"Cancel"}
              </ProButton>
              <ProButton onClick={handleSubmit(handleSave)}>{"Save"}</ProButton>
            </Grid>
          </Grid>
        </Box>
      </Modal>
    </>
  );
}

UploadFileAppendixD.propTypes = {
  title: PropTypes.string,
  getDetailData: PropTypes.func
};